function publishStorefrontFilterEvent(eventData) {
  try {
    if (!window.Shopify || !Shopify.analytics || typeof Shopify.analytics.publish !== 'function') return;

    const publishResult = Shopify.analytics.publish('storefront_filter_changed', eventData);
    if (publishResult && typeof publishResult.catch === 'function') publishResult.catch(() => {});
  } catch (error) {
    // Analytics must never interrupt storefront filtering.
  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    facetForm.addEventListener('submit', this.onFullPageSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true, trackingEvents = []) {
    if (updateURLHash) {
      // Internal filters remain selected when visible filters/sorts or clear-all change.
      const next = new URLSearchParams(searchParams);
      const current = new URLSearchParams(window.location.search);
      const hidden = (document.querySelector('[data-hidden-filter-params]')?.dataset.hiddenFilterParams || '').split(',');
      for (const name of new Set(hidden.filter(Boolean))) {
        if (!next.has(name)) current.getAll(name).forEach(value => next.append(name, value));
      }
      searchParams = next.toString();
    }
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, trackingEvents)
        : FacetFiltersForm.renderSectionFromFetch(url, event, trackingEvents);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, trackingEvents) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        const resultsCount = FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.publishTrackingEvents(trackingEvents, resultsCount);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, trackingEvents) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    const resultsCount = FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.publishTrackingEvents(trackingEvents, resultsCount);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const parsedCount = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const count = parsedCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.dataset.resultsCount = parsedCount.dataset.resultsCount;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    return Number(parsedCount.dataset.resultsCount);
  }

  static getFilterContext() {
    const container = document.querySelector('.facets-container');

    return {
      page_type: container ? container.dataset.filterPageType || '' : '',
      collection_handle: container ? container.dataset.collectionHandle || '' : '',
      search_term: container ? container.dataset.searchTerm || '' : '',
    };
  }

  static getFilterMetadata() {
    const values = new Map();
    const priceRanges = new Map();

    document.querySelectorAll('.js-filter[data-filter-name]').forEach((filter) => {
      const filterName = filter.dataset.filterName || '';

      filter.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach((input) => {
        values.set(`${input.name}\u0000${input.value}`, {
          filterName,
          filterValue: input.dataset.filterValue || '',
        });
      });

      const priceRange = filter.querySelector('price-range');
      if (!priceRange) return;

      const inputs = Array.from(priceRange.querySelectorAll('input[name^="filter."]'));
      if (!inputs.length) return;

      const groupName = inputs[0].name.replace(/\.(gte|lte)$/, '');
      if (priceRanges.has(groupName)) return;

      priceRanges.set(groupName, {
        filterName,
        currency: priceRange.querySelector('.field-currency')?.textContent.trim() || '',
        max: inputs.find((input) => input.name.endsWith('.lte'))?.max || '',
        parameters: inputs.map((input) => input.name),
      });
    });

    return { values, priceRanges };
  }

  static getFilterEntries(searchParams) {
    const entries = new Set();

    new URLSearchParams(searchParams).forEach((value, parameter) => {
      if (parameter.startsWith('filter.') && value !== '') entries.add(`${parameter}\u0000${value}`);
    });

    return entries;
  }

  static countActiveFilters(searchParams) {
    const activeFilters = new Set();
    const hidden = (document.querySelector('[data-hidden-filter-params]')?.dataset.hiddenFilterParams || '').split(',');

    new URLSearchParams(searchParams).forEach((value, parameter) => {
      if (!parameter.startsWith('filter.') || value === '' || hidden.includes(parameter)) return;

      const priceGroup = parameter.match(/^(.*)\.(gte|lte)$/);
      activeFilters.add(priceGroup ? priceGroup[1] : `${parameter}\u0000${value}`);
    });

    return activeFilters.size;
  }

  static formatPriceRange(priceRange, searchParams) {
    const params = new URLSearchParams(searchParams);
    const minParameter = priceRange.parameters.find((parameter) => parameter.endsWith('.gte'));
    const maxParameter = priceRange.parameters.find((parameter) => parameter.endsWith('.lte'));
    const min = (minParameter && params.get(minParameter)) || '0';
    const max = (maxParameter && params.get(maxParameter)) || priceRange.max;

    return {
      value: `${priceRange.currency}${min}\u2013${priceRange.currency}${max}`,
      rawValue: `${min}-${max}`,
    };
  }

  static createTrackingEvents(previousSearchParams, nextSearchParams, source) {
    const previousParams = new URLSearchParams(previousSearchParams);
    const nextParams = new URLSearchParams(nextSearchParams);
    const activeFilterCount = FacetFiltersForm.countActiveFilters(nextParams);
    const { values, priceRanges } = FacetFiltersForm.getFilterMetadata();
    const isClearAll = source?.classList.contains('active-facets__button-remove') ||
      source?.dataset.clearAllFilters === 'true';

    if (isClearAll) {
      if (FacetFiltersForm.countActiveFilters(previousParams) === 0) return [];

      return [{
        action: 'clear',
        filter_name: '',
        filter_parameter: '',
        filter_value: '',
        filter_raw_value: '',
        active_filter_count: activeFilterCount,
      }];
    }

    const previousEntries = FacetFiltersForm.getFilterEntries(previousParams);
    const nextEntries = FacetFiltersForm.getFilterEntries(nextParams);
    const trackingEvents = [];

    previousEntries.forEach((entry) => {
      const [parameter, rawValue] = entry.split('\u0000');
      if (nextEntries.has(entry) || priceRanges.has(parameter.replace(/\.(gte|lte)$/, ''))) return;

      const metadata = values.get(entry);
      if (!metadata) return;

      trackingEvents.push({
        action: 'remove',
        filter_name: metadata.filterName,
        filter_parameter: parameter,
        filter_value: metadata.filterValue,
        filter_raw_value: rawValue,
        active_filter_count: activeFilterCount,
      });
    });

    nextEntries.forEach((entry) => {
      const [parameter, rawValue] = entry.split('\u0000');
      if (previousEntries.has(entry) || priceRanges.has(parameter.replace(/\.(gte|lte)$/, ''))) return;

      const metadata = values.get(entry);
      if (!metadata) return;

      trackingEvents.push({
        action: 'apply',
        filter_name: metadata.filterName,
        filter_parameter: parameter,
        filter_value: metadata.filterValue,
        filter_raw_value: rawValue,
        active_filter_count: activeFilterCount,
      });
    });

    priceRanges.forEach((priceRange) => {
      const changedParameters = priceRange.parameters.filter(
        (parameter) => previousParams.get(parameter) !== nextParams.get(parameter)
      );
      if (!changedParameters.length) return;

      const sourceParameter = source?.name && priceRange.parameters.includes(source.name) ? source.name : null;
      const filterParameter = sourceParameter || changedParameters[0];
      const previousActive = priceRange.parameters.some((parameter) => previousParams.get(parameter));
      const nextActive = priceRange.parameters.some((parameter) => nextParams.get(parameter));
      const removedBoundary = previousParams.get(filterParameter) && !nextParams.get(filterParameter);
      const action = !nextActive || removedBoundary ? 'remove' : 'apply';
      const displayRange = FacetFiltersForm.formatPriceRange(
        priceRange,
        action === 'remove' ? previousParams : nextParams
      );

      if (!previousActive && !nextActive) return;

      trackingEvents.push({
        action,
        filter_name: priceRange.filterName,
        filter_parameter: filterParameter,
        filter_value: displayRange.value,
        filter_raw_value: displayRange.rawValue,
        active_filter_count: activeFilterCount,
      });
    });

    return trackingEvents;
  }

  static publishTrackingEvents(trackingEvents, resultsCount) {
    if (!trackingEvents.length) return;

    const context = FacetFiltersForm.getFilterContext();
    trackingEvents.forEach((trackingEvent) => {
      publishStorefrontFilterEvent({
        ...trackingEvent,
        ...context,
        results_count: resultsCount,
      });
    });
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const incomingFacets = parsedHTML.querySelector('[data-hidden-filter-params]');
    const currentFacets = document.querySelector('[data-hidden-filter-params]');
    if (incomingFacets && currentFacets) currentFacets.dataset.hiddenFilterParams = incomingFacets.dataset.hiddenFilterParams;
    document.querySelectorAll('facet-filters-form form').forEach(form => {
      const source = parsedHTML.getElementById(form.id);
      if (!source) return;
      form.querySelectorAll('[data-hidden-filter-input]').forEach(input => input.remove());
      source.querySelectorAll('[data-hidden-filter-input]').forEach(input => form.append(input.cloneNode(true)));
    });
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };
    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);
        if (newElementToActivate) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    const trackingEvents = FacetFiltersForm.createTrackingEvents(
      FacetFiltersForm.searchParamsPrev,
      searchParams,
      event.target
    );
    FacetFiltersForm.renderPage(searchParams, event, true, trackingEvents);
  }

  onSubmitHandler(event) {
    if (this.fullPageSubmitting) return;

    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onFullPageSubmit(event) {
    this.fullPageSubmitting = true;
    const hidden = (document.querySelector('[data-hidden-filter-params]')?.dataset.hiddenFilterParams || '').split(',');
    for (const [name, value] of new URLSearchParams(window.location.search)) {
      if (hidden.includes(name) && !Array.from(event.currentTarget.elements).some(input => input.name === name && input.value === value)) {
        const input = document.createElement('input');
        input.type = 'hidden'; input.name = name; input.value = value;
        event.currentTarget.append(input);
      }
    }
    const searchParams = this.createSearchParams(event.currentTarget);
    const trackingEvents = FacetFiltersForm.createTrackingEvents(
      FacetFiltersForm.searchParamsPrev,
      searchParams,
      event.submitter || event.currentTarget
    );
    const resultsCount = Number(document.getElementById('ProductCount')?.dataset.resultsCount);

    FacetFiltersForm.publishTrackingEvents(trackingEvents, resultsCount);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    const trackingEvents = FacetFiltersForm.createTrackingEvents(
      FacetFiltersForm.searchParamsPrev,
      url,
      event.currentTarget
    );
    FacetFiltersForm.renderPage(url, null, true, trackingEvents);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
